import { useContext, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { PrimaryObjectUploadContext } from '../../context';
import { StyledSubtitle, StyledCols, StyledNotice } from './styles';
import { AutomationOption, AutomationSelect } from './AutomationSelect';
import Select from '__components/Inputs/Select';
import { useVariableErrors } from './useVariableErrors';
import {
  getSmartConnectorVariables,
  getUniqueName,
  getVariableNameError,
  isCSVLoadStep,
} from 'pages/SmartConnectors/SmartConnector/helpers';
import type {
  AutomationTriggerConfig,
  CSVFlowLoad,
  GenericOption,
  SmartConnectorFlowLoad,
  SmartVariableDataType,
} from 'pages/SmartConnectors/types';
import {
  MAX_VARIABLE_NAME_LENGTH,
  VARIABLE_TYPES,
} from 'pages/SmartConnectors/constants';
import { getAutomationTriggerOptions, getVariableTypeOptions } from './helpers';
import { VariableNameInput } from 'pages/SmartConnectors/SmartConnector/VariableNameInput';

const isCurrentLoad = (
  load: SmartConnectorFlowLoad,
  id: string
): load is CSVFlowLoad => {
  return load.id === id;
};

export const Settings = () => {
  const { t } = useTranslation();
  const {
    scope,
    stepData,
    setStepData,
    loadId,
    loadIndex,
    smartConnector,
    customObject,
    allVariables,
    setErrors,
  } = useContext(PrimaryObjectUploadContext);

  const currentLoad = stepData.loads?.find(
    (load): load is CSVFlowLoad => load.id === loadId
  );

  const { hasError, validate, errorKey, clearError } = useVariableErrors();

  const variableTypeOptions = useMemo(
    () => getVariableTypeOptions(customObject, t),
    [t, customObject]
  );

  const automationTriggerOptions = useMemo(
    () => getAutomationTriggerOptions(t),
    [t]
  );

  useEffect(() => {
    const variableName = getUniqueName(
      `${customObject?.name.toLowerCase().replaceAll(/[^a-z0-9]/g, '_')}_matched_record`.slice(
        0,
        MAX_VARIABLE_NAME_LENGTH
      ),
      allVariables.map((v) => v.name)
    );
    setStepData((prev) => ({
      ...prev,
      loads: (prev.loads || []).map((load) => {
        if (isCurrentLoad(load, loadId) && !load.execution_variable) {
          return {
            ...load,
            execution_variable: {
              id: `new_${Date.now()}`,
              data_type: variableTypeOptions[0]?.value || null,
              name: variableName,
              type: VARIABLE_TYPES.related_object_upload,
            },
          };
        }
        return load;
      }),
    }));

    const nameError = getVariableNameError(variableName, t);

    setErrors((prev) => ({
      ...prev,
      loads: (prev.loads || []).map((load, i) => {
        if (i === loadIndex) {
          return {
            ...load,
            execution_variable: nameError ? { name: [nameError] } : {},
          };
        }
        return load;
      }),
    }));
  }, [
    allVariables,
    smartConnector.custom_object,
    customObject,
    setStepData,
    t,
    loadId,
    variableTypeOptions,
    setErrors,
    loadIndex,
  ]);

  const onChangeAutomationsForCreated = (value: AutomationOption[]) => {
    setStepData((prev) => ({
      ...prev,
      loads: (prev.loads || []).map((load) => {
        if (load.id === loadId) {
          return {
            ...load,
            newly_created_records_automations: value.map(
              ({ value, label, originalLabel, error = false }) => ({
                id: value,
                name: originalLabel || label,
                active: !error,
              })
            ),
          };
        }
        return load;
      }),
    }));
  };

  const onChangeAutomationsForMatched = (value: AutomationOption[]) => {
    setStepData((prev) => ({
      ...prev,
      loads: (prev.loads || []).map((load) => {
        if (load.id === loadId) {
          return {
            ...load,
            other_matches_records_automations: value.map(
              ({ value, label, originalLabel, error = false }) => ({
                id: value,
                name: originalLabel || label,
                active: !error,
              })
            ),
          };
        }
        return load;
      }),
    }));
  };

  const onChnageAutomationTrigger = ({
    value,
  }: GenericOption<AutomationTriggerConfig>) => {
    setStepData((prev) => ({
      ...prev,
      loads: (prev.loads || []).map((load) => {
        if (load.id === loadId) {
          return {
            ...load,
            automation_trigger_config: value,
          };
        }
        return load;
      }),
    }));
  };

  const onChangeVariableName = (value: string) => {
    // load variable name should be unique within the scope
    const names = getSmartConnectorVariables({
      execution_variables: (smartConnector.execution_variables || []).filter(
        (v) => v.scope === scope
      ),
      flow: {
        ...smartConnector.flow,
        loads: (smartConnector.flow.loads || []).reduce<
          SmartConnectorFlowLoad[]
        >((acc, load) => {
          if (load.id === loadId) {
            return acc;
          }
          if (isCSVLoadStep(load) && load.scope === scope) {
            acc.push(load);
          }
          if (!isCSVLoadStep(load) && load.return_scopes?.length) {
            const returnScopes = load.return_scopes.filter(
              (returnScope) => returnScope.name === scope
            );
            if (returnScopes.length) {
              acc.push({ ...load, return_scopes: returnScopes });
            }
          }
          return acc;
        }, []),
      },
    }).map(({ name }) => name);
    const name = getUniqueName(value.trim(), names);
    setStepData((prev) => ({
      ...prev,
      loads: (prev.loads || []).map((load) => {
        if (isCurrentLoad(load, loadId)) {
          return {
            ...load,
            execution_variable: {
              id: load.execution_variable?.id || `new_${Date.now()}`,
              data_type: load.execution_variable?.data_type || null,
              name,
              type: VARIABLE_TYPES.related_object_upload,
            },
          };
        }
        return load;
      }),
    }));

    clearError('name');

    const nameError = getVariableNameError(name, t);

    if (nameError) {
      setTimeout(
        () =>
          setErrors((prev) => ({
            ...prev,
            loads: (prev.loads || []).map((load, i) => {
              if (i === loadIndex) {
                return {
                  ...load,
                  execution_variable: {
                    ...load.execution_variable,
                    name: [nameError],
                  },
                };
              }
              return load;
            }),
          })),
        0
      );
    }

    return name;
  };

  const onChangeVariableDataType = ({
    value,
  }: {
    value: SmartVariableDataType;
  }) => {
    setStepData((prev) => ({
      ...prev,
      loads: (prev.loads || []).map((load) => {
        if (isCurrentLoad(load, loadId)) {
          return {
            ...load,
            execution_variable: {
              id: load.execution_variable?.id || `new_${Date.now()}`,
              name: load.execution_variable?.name || '',
              data_type: value,
              type: VARIABLE_TYPES.related_object_upload,
            },
          };
        }
        return load;
      }),
    }));

    clearError('data_type');
  };

  return (
    <>
      <StyledSubtitle weight="bold">{t('Settings')}</StyledSubtitle>
      <StyledNotice as="div">
        {t(
          'Once a record has been matched on this object, and all functions of this upload step have been completed for the respective row, the record that the actions were completed on will need to be assigned to a variable for use in following upload steps. You may also start agentic workflows on newly created or other matched records at the conclusion of the SmartConnector processing.'
        )}
      </StyledNotice>
      <StyledCols columns={2} gutter="20px">
        <VariableNameInput
          value={currentLoad?.execution_variable?.name || ''}
          label={t(
            'Variable Name to Assign Output Record (Created or Matched)'
          )}
          onChange={onChangeVariableName}
          error={!!hasError && errorKey === 'name'}
          validate={errorKey === 'name' ? validate : undefined}
        />
        <Select
          value={currentLoad?.execution_variable?.data_type}
          onChange={onChangeVariableDataType}
          label={t('Variable Type')}
          options={variableTypeOptions}
          margin
        />
      </StyledCols>
      <StyledCols columns={2} gutter="20px">
        <AutomationSelect
          customObjectId={currentLoad?.custom_object!}
          selected={currentLoad?.newly_created_records_automations || []}
          onChange={onChangeAutomationsForCreated}
          label={t('Agentic Workflow(s) to Start on Newly Created Records')}
          labelInfo={t(
            'Agentic Workflows will only run once on records created per upload. The agentic workflow will start once the entire upload has finished processing.'
          )}
        />
        <AutomationSelect
          customObjectId={currentLoad?.custom_object!}
          selected={currentLoad?.other_matches_records_automations || []}
          onChange={onChangeAutomationsForMatched}
          label={t('Agentic Workflow(s) to Start on Other Matched Records')}
          labelInfo={t(
            'Agentic Workflows will only run once, and only on matched records that were not created as part of this upload. The agentic workflow will start once the entire upload has finished processing.'
          )}
        />
      </StyledCols>
      <StyledCols columns={2} gutter="20px">
        <Select
          value={currentLoad?.automation_trigger_config}
          onChange={onChnageAutomationTrigger}
          label={t('Agentic Workflow Trigger Suppression')}
          options={automationTriggerOptions}
        />
      </StyledCols>
    </>
  );
};
